import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type RoadmapItem, type RoadmapTriageEntry, useMergeRoadmapTriage } from "@/lib/api";
import { inlineFailure } from "@/lib/api-errors";

import { Field } from "./item-form";
import { ItemPicker } from "./item-picker";
import { StepButtons } from "./step-buttons";
import { mergeFailureMessage, mergeTargets, triageKind } from "./triage";
import { TriageEntryTitle, useTriageReturnFocus } from "./triage-dialog-parts";

interface MergeTriageDialogProps {
  /** The entry to merge; it stays while the dialog closes. */
  entry: RoadmapTriageEntry | null;
  /**
   * The entry has left the triage since the page loaded (`triageEntryGone`):
   * another developer, or Claude, handled it. The form says so and merges nothing.
   */
  entryGone?: boolean;
  /** The roadmap's items in its order; those not done are offered (`mergeTargets`). */
  items: readonly RoadmapItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Samenvoegen met… on a triage entry: the entry's title, a searchable choice
 * of the items on the roadmap that are not done, and Annuleren or
 * Samenvoegen. The entry leaves the triage, linked to the item chosen, and
 * whoever submitted an idea follows that item when they see the roadmap (the
 * server's rule). Should the entry leave the triage while the dialog is open,
 * the form says so and Samenvoegen stays off. The focus goes back as after
 * Sluiten (`useTriageReturnFocus`).
 */
export function MergeTriageDialog({
  entry,
  entryGone = false,
  items,
  open,
  onOpenChange,
}: MergeTriageDialogProps) {
  const { t } = useTranslation();
  const onCloseAutoFocus = useTriageReturnFocus(open, entry);
  const kind = entry ? triageKind(entry) : "idea";
  return (
    <Dialog open={open && entry !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{t(`roadmap.triage.mergeTitle.${kind}`)}</DialogTitle>
          <DialogDescription>{t(`roadmap.triage.mergeDescription.${kind}`)}</DialogDescription>
        </DialogHeader>
        {/* Mounted while open only, so every opening starts with no item chosen. */}
        {open && entry && (
          <MergeTriageForm
            key={entry.id}
            entry={entry}
            entryGone={entryGone}
            items={items}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface MergeTriageFormProps {
  entry: RoadmapTriageEntry;
  /** The entry has left the triage meanwhile: handled elsewhere. */
  entryGone: boolean;
  items: readonly RoadmapItem[];
  /** Back to the table: after the merge went through, or on Annuleren. */
  onDone: () => void;
}

/** The merge's form: the item to merge into, and the failure of the last attempt. */
export function MergeTriageForm({ entry, entryGone, items, onDone }: MergeTriageFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const merge = useMergeRoadmapTriage();
  const [picked, setPicked] = useState<string | null>(null);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const targets = mergeTargets(items);
  // The item picked while it is still offered: one finished or deleted meanwhile drops out.
  const chosen = targets.some((item) => item.id === picked) ? picked : null;
  // Left the triage, and not through this form's own merge (which ends the dialog once the roadmap shows it).
  const handled = entryGone && (merge.isIdle || merge.isError);

  const submit = async () => {
    if (merge.isPending || handled || chosen === null) {
      return;
    }
    setFailure(null);
    try {
      await merge.mutateAsync({ entryId: entry.id, itemId: chosen });
      onDone();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <TriageEntryTitle entry={entry} />
      <Field
        label={t("roadmap.triage.mergeItem")}
        htmlFor={`${ids}-item`}
        hint={t("roadmap.triage.mergeItemHint")}
      >
        <ItemPicker
          id={`${ids}-item`}
          items={targets}
          value={chosen}
          onChange={(itemId) => {
            setPicked(itemId);
            // A failure about the item before no longer holds for another.
            setFailure(null);
          }}
          label={t("roadmap.triage.mergeChoose")}
          className="w-full"
        />
      </Field>
      {handled ? (
        <p role="alert" className="text-destructive text-sm">
          {t("errors.codes.roadmap_triage_not_found")}
        </p>
      ) : (
        failure && (
          <p role="alert" className="text-destructive text-sm">
            {mergeFailureMessage(failure.error)}
          </p>
        )
      )}
      <StepButtons
        onBack={onDone}
        confirm={t("roadmap.triage.mergeConfirm")}
        pending={merge.isPending}
        disabled={handled || chosen === null}
      />
    </form>
  );
}
